/**
 * EsignConsent — shared electronic-signature consent disclosure + agree action.
 *
 * Sources its copy from docs/policies/esign-consent.md (the canonical ESIGN disclosure,
 * committed at 6d6e2939) via a build-time Vite `?raw` import — the same mechanism
 * apps/web/src/pages/PolicyPage.tsx uses for terms-of-use.md/privacy-policy.md. Renders
 * with @uiw/react-md-editor's `MDEditor.Markdown`, this app's existing markdown-rendering
 * approach (see PolicyModal.tsx, RulesSection.tsx) — no new dependency added.
 *
 * Rendered by BOTH PublicSigningPage.tsx and SigningEnvelopeDetailPage.tsx so the two entry
 * points never duplicate the disclosure text (BR-DS-02). Each page keeps its own heading,
 * container, and focus-management wiring; this component owns only the disclosure body and
 * the "I Agree" action, with an optional secondary action (e.g. a page-specific Cancel
 * button) slotted in alongside it.
 */

import MDEditor from '@uiw/react-md-editor';
import { MarkdownThemeScope } from '../MarkdownThemeScope';
import type { ReactNode } from 'react';
import { Button } from '../ui/button';
import esignConsentMd from '../../../../../docs/policies/esign-consent.md?raw';

// The canonical file opens with an H1 title and Disclosure ID/Effective Date/Last Updated
// metadata lines that are NOT part of the user-facing disclosure (each page renders its own
// heading), and closes with a '---' divider followed by an HTML comment block of
// maintainer-only guidance (never user-facing copy). Keep only the body paragraph, bullet
// list, and closing sentence between them — verbatim, byte-equivalent to what ships today.
const ESIGN_CONSENT_BODY = (esignConsentMd.split(/\n---\n/)[0] ?? esignConsentMd)
  .replace(/^#.*\n+(?:\*\*.*\n)+\n*/, '')
  .trim();

export interface EsignConsentProps {
  /** Invoked when the signer clicks the primary "I Agree" action. */
  onAgree: () => void;
  /** data-testid for the primary agree button — differs per page, so it's required. */
  agreeTestId: string;
  agreeLabel?: string;
  agreePendingLabel?: string;
  agreePending?: boolean;
  agreeDisabled?: boolean;
  agreeAriaLabel?: string;
  agreeButtonClassName?: string;
  /** Extra control rendered alongside the agree button (e.g. a page-specific Cancel button). */
  secondaryAction?: ReactNode;
}

export function EsignConsent({
  onAgree,
  agreeTestId,
  agreeLabel = 'I Agree — Proceed to Sign',
  agreePendingLabel = 'Processing...',
  agreePending = false,
  agreeDisabled = false,
  agreeAriaLabel,
  agreeButtonClassName,
  secondaryAction,
}: EsignConsentProps) {
  return (
    <>
      <MarkdownThemeScope className="text-sm text-muted-foreground space-y-3 mb-6">
        <MDEditor.Markdown source={ESIGN_CONSENT_BODY} style={{ backgroundColor: 'transparent' }} />
      </MarkdownThemeScope>
      <div className="flex gap-3">
        <Button
          data-testid={agreeTestId}
          aria-label={agreeAriaLabel}
          onClick={onAgree}
          disabled={agreeDisabled}
          className={agreeButtonClassName}
        >
          {agreePending ? agreePendingLabel : agreeLabel}
        </Button>
        {secondaryAction}
      </div>
    </>
  );
}

export default EsignConsent;
